import { useCallback, useEffect, useMemo, useState } from 'react';
import { getEmotionAnalytics } from '../../../api/home';
import type { AnalyticsResponse } from '../../../api/types';
import { useAnonymousUser } from '../../../hooks/useAnonymousUser';
import PullToRefresh from './PullToRefresh';

interface AnalyticsScreenProps {
  onBack: () => void;
}

type PeriodId = '7d' | '30d' | 'month' | 'year' | 'custom';

const PERIODS: { id: PeriodId; label: string }[] = [
  { id: '7d', label: '7 дней' },
  { id: '30d', label: '30 дней' },
  { id: 'month', label: 'Месяц' },
  { id: 'year', label: 'Год' },
  { id: 'custom', label: 'Период' },
];

const EMOTIONS = [
  { key: 'joy', label: 'Радость', color: '#3ECFA0' },
  { key: 'sadness', label: 'Печаль', color: '#4A90E2' },
  { key: 'fear', label: 'Страх', color: '#8B7AB8' },
  { key: 'anger', label: 'Гнев', color: '#FF6B6B' },
];

function buildPath(data: number[], w: number, h: number, max = 10): string {
  const n = data.length;
  if (n === 0) return '';
  const xs = data.map((_, i) => (n === 1 ? w / 2 : (i / (n - 1)) * w));
  const ys = data.map((v) => h - (v / max) * h);
  let d = `M ${xs[0]} ${ys[0]}`;
  for (let i = 1; i < n; i++) {
    const cp1x = xs[i - 1] + (xs[i] - xs[i - 1]) * 0.5;
    const cp1y = ys[i - 1];
    const cp2x = xs[i - 1] + (xs[i] - xs[i - 1]) * 0.5;
    const cp2y = ys[i];
    d += ` C ${cp1x} ${cp1y}, ${cp2x} ${cp2y}, ${xs[i]} ${ys[i]}`;
  }
  return d;
}

function buildAreaPath(data: number[], w: number, h: number, max = 10): string {
  const n = data.length;
  if (n === 0) return '';
  const xs = data.map((_, i) => (n === 1 ? w / 2 : (i / (n - 1)) * w));
  const ys = data.map((v) => h - (v / max) * h);
  let d = `M ${xs[0]} ${h} L ${xs[0]} ${ys[0]}`;
  for (let i = 1; i < n; i++) {
    const cp1x = xs[i - 1] + (xs[i] - xs[i - 1]) * 0.5;
    const cp2x = cp1x;
    d += ` C ${cp1x} ${ys[i - 1]}, ${cp2x} ${ys[i]}, ${xs[i]} ${ys[i]}`;
  }
  d += ` L ${xs[n - 1]} ${h} Z`;
  return d;
}

// Simple month calendar for date range picker
const MONTH_NAMES = ['Январь','Февраль','Март','Апрель','Май','Июнь','Июль','Август','Сентябрь','Октябрь','Ноябрь','Декабрь'];

function getDaysInMonth(year: number, month: number) {
  return new Date(year, month + 1, 0).getDate();
}
function getFirstDayOfWeek(year: number, month: number) {
  const day = new Date(year, month, 1).getDay();
  return day === 0 ? 6 : day - 1; // Mon=0
}

function toDateInputValue(date: Date): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

function chartLabel(value: string, index: number, total: number): string {
  const date = new Date(`${value}T00:00:00`);
  if (total <= 8) {
    return new Intl.DateTimeFormat('ru-RU', { weekday: 'short' }).format(date).slice(0, 2);
  }
  if (total <= 45) {
    return index === 0 || index === total - 1 || index % 5 === 0 ? String(date.getDate()) : '';
  }
  return index === 0 || index === total - 1 || index % 30 === 0
    ? new Intl.DateTimeFormat('ru-RU', { month: 'short' }).format(date).replace('.', '')
    : '';
}

function formatShortDate(value: string): string {
  return new Intl.DateTimeFormat('ru-RU', { day: 'numeric', month: 'short' })
    .format(new Date(`${value}T00:00:00`))
    .replace('.', '');
}

interface DateRangePickerProps {
  startDate: Date | null;
  endDate: Date | null;
  onSelect: (start: Date, end: Date | null) => void;
}

function DateRangePicker({ startDate, endDate, onSelect }: DateRangePickerProps) {
  const today = new Date();
  const [viewYear, setViewYear] = useState(today.getFullYear());
  const [viewMonth, setViewMonth] = useState(today.getMonth());

  const daysInMonth = getDaysInMonth(viewYear, viewMonth);
  const firstDay = getFirstDayOfWeek(viewYear, viewMonth);

  function prevMonth() {
    if (viewMonth === 0) { setViewMonth(11); setViewYear(y => y - 1); }
    else setViewMonth(m => m - 1);
  }
  function nextMonth() {
    if (viewMonth === 11) { setViewMonth(0); setViewYear(y => y + 1); }
    else setViewMonth(m => m + 1);
  }

  function handleDayClick(day: number) {
    const clicked = new Date(viewYear, viewMonth, day);
    if (!startDate || (startDate && endDate)) {
      onSelect(clicked, null);
    } else {
      if (clicked < startDate) {
        onSelect(clicked, startDate);
      } else {
        onSelect(startDate, clicked);
      }
    }
  }

  function inRange(day: number): boolean {
    if (!startDate || !endDate) return false;
    const d = new Date(viewYear, viewMonth, day);
    return d > startDate && d < endDate;
  }
  function isStart(day: number): boolean {
    if (!startDate) return false;
    return startDate.getFullYear() === viewYear && startDate.getMonth() === viewMonth && startDate.getDate() === day;
  }
  function isEnd(day: number): boolean {
    if (!endDate) return false;
    return endDate.getFullYear() === viewYear && endDate.getMonth() === viewMonth && endDate.getDate() === day;
  }

  return (
    <div
      style={{
        background: 'rgba(30,32,55,0.98)',
        borderRadius: '20px',
        padding: '20px',
        border: '1px solid rgba(255,255,255,0.1)',
      }}
    >
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '16px' }}>
        <button
          onClick={prevMonth}
          style={{ width: '32px', height: '32px', borderRadius: '50%', background: 'rgba(255,255,255,0.08)', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
        >
          <i className="ri-arrow-left-s-line" style={{ color: '#F5F5F7', fontSize: '18px' }} />
        </button>
        <span style={{ fontSize: '15px', fontWeight: 600, color: '#F5F5F7' }}>
          {MONTH_NAMES[viewMonth]} {viewYear}
        </span>
        <button
          onClick={nextMonth}
          style={{ width: '32px', height: '32px', borderRadius: '50%', background: 'rgba(255,255,255,0.08)', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
        >
          <i className="ri-arrow-right-s-line" style={{ color: '#F5F5F7', fontSize: '18px' }} />
        </button>
      </div>

      {/* Week headers */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', marginBottom: '6px' }}>
        {['Пн','Вт','Ср','Чт','Пт','Сб','Вс'].map(d => (
          <div key={d} style={{ textAlign: 'center', fontSize: '11px', color: '#6B7A94', padding: '2px 0' }}>{d}</div>
        ))}
      </div>

      {/* Days */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: '2px' }}>
        {Array.from({ length: firstDay }).map((_, i) => <div key={`e-${i}`} />)}
        {Array.from({ length: daysInMonth }, (_, i) => {
          const day = i + 1;
          const selected = isStart(day) || isEnd(day);
          const range = inRange(day);
          return (
            <button
              key={day}
              onClick={() => handleDayClick(day)}
              style={{
                aspectRatio: '1',
                borderRadius: selected ? '50%' : range ? '0' : '50%',
                border: 'none',
                cursor: 'pointer',
                fontSize: '13px',
                fontWeight: selected ? 600 : 400,
                color: selected ? '#fff' : range ? '#3ECFA0' : '#A8B2C8',
                background: selected
                  ? 'linear-gradient(135deg, #3ECFA0, #2BA87F)'
                  : range
                  ? 'rgba(62,207,160,0.12)'
                  : 'transparent',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                padding: 0,
              }}
            >
              {day}
            </button>
          );
        })}
      </div>

      {/* Selection hint */}
      <p style={{ fontSize: '12px', color: '#6B7A94', marginTop: '14px', textAlign: 'center' }}>
        {!startDate
          ? 'Выберите начальную дату'
          : !endDate
          ? 'Выберите конечную дату'
          : `${startDate.toLocaleDateString('ru-RU')} — ${endDate.toLocaleDateString('ru-RU')}`}
      </p>
    </div>
  );
}

export default function AnalyticsScreen({ onBack }: AnalyticsScreenProps) {
  const { userId, loading: userLoading, error: userError } = useAnonymousUser();
  const [period, setPeriod] = useState<PeriodId>('7d');
  const [visibleEmotions, setVisibleEmotions] = useState<Set<string>>(
    new Set(['joy', 'sadness', 'fear', 'anger'])
  );
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [startDate, setStartDate] = useState<Date | null>(null);
  const [endDate, setEndDate] = useState<Date | null>(null);
  const [analytics, setAnalytics] = useState<AnalyticsResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);

  const W = 300;
  const H = 160;

  const shouldFetch = period !== 'custom' || Boolean(startDate && endDate);
  const startDateIso = startDate ? toDateInputValue(startDate) : null;
  const endDateIso = endDate ? toDateInputValue(endDate) : null;

  const refreshAnalytics = useCallback(async () => {
    if (!userId || !shouldFetch) return;

    setLoading(true);
    setLoadError(null);

    try {
      const data = await getEmotionAnalytics({
        userId,
        period,
        startDate: startDateIso,
        endDate: endDateIso,
      });
      setAnalytics(data);
    } catch (error) {
      setLoadError(error instanceof Error ? error.message : 'Не удалось загрузить аналитику');
    } finally {
      setLoading(false);
    }
  }, [endDateIso, period, shouldFetch, startDateIso, userId]);

  useEffect(() => {
    if (!userId || !shouldFetch) return;

    let cancelled = false;
    setLoading(true);
    setLoadError(null);

    getEmotionAnalytics({
      userId,
      period,
      startDate: startDateIso,
      endDate: endDateIso,
    })
      .then((data) => {
        if (cancelled) return;
        setAnalytics(data);
        setLoading(false);
      })
      .catch((error) => {
        if (cancelled) return;
        setLoadError(error instanceof Error ? error.message : 'Не удалось загрузить аналитику');
        setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [userId, period, startDateIso, endDateIso, shouldFetch]);

  const dataMap: Record<string, number[]> = useMemo(() => ({
    joy: analytics?.points.map((point) => point.joy) ?? [],
    sadness: analytics?.points.map((point) => point.sadness) ?? [],
    fear: analytics?.points.map((point) => point.fear) ?? [],
    anger: analytics?.points.map((point) => point.anger) ?? [],
  }), [analytics]);

  const labels = useMemo(
    () => analytics?.points.map((point, index) => chartLabel(point.date, index, analytics.points.length)) ?? [],
    [analytics],
  );

  function toggleEmotion(key: string) {
    setVisibleEmotions(prev => {
      const next = new Set(prev);
      if (next.has(key)) {
        if (next.size > 1) next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });
  }

  function handlePeriodSelect(id: PeriodId) {
    setPeriod(id);
    if (id === 'custom') {
      setShowDatePicker(true);
    } else {
      setShowDatePicker(false);
    }
  }

  function handleDateSelect(start: Date, end: Date | null) {
    setStartDate(start);
    setEndDate(end);
  }

  // Dominant emotion for insight
  const dominantEmotion = useMemo(() => {
    const avgs = EMOTIONS.map(e => ({ ...e, avg: analytics?.averages[e.key] ?? 0 }));
    return avgs.sort((a, b) => b.avg - a.avg)[0];
  }, [analytics]);

  // Stats cards
  const statsData = useMemo(() => EMOTIONS.map(e => ({
    ...e,
    avg: analytics?.averages[e.key] ?? 0,
    max: analytics?.maximums[e.key] ?? 0,
  })), [analytics]);

  const hasChartValues = analytics?.points.some((point) => point.memories_count > 0) ?? false;
  const effectiveError = userError || loadError;

  return (
    <PullToRefresh
      onRefresh={refreshAnalytics}
      disabled={userLoading || loading || !userId || !shouldFetch}
    >
      <div
        style={{
          minHeight: '100%',
          background: 'linear-gradient(160deg, #1A1B2E 0%, #2D1B3D 100%)',
          paddingBottom: '32px',
        }}
      >
        {/* Top nav */}
        <div
          style={{
            position: 'sticky',
            top: 0,
            zIndex: 10,
            height: '56px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: '0 20px',
            background: 'rgba(26,27,46,0.97)',
            borderBottom: '1px solid rgba(255,255,255,0.06)',
            backdropFilter: 'blur(20px)',
          }}
        >
          <button
            onClick={onBack}
            style={{
              width: '32px', height: '32px', borderRadius: '50%',
              background: 'rgba(255,255,255,0.08)', border: 'none', cursor: 'pointer',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}
          >
            <i className="ri-arrow-left-s-line" style={{ color: '#F5F5F7', fontSize: '20px' }} />
          </button>
          <span style={{ fontSize: '18px', fontWeight: 600, color: '#F5F5F7' }}>Аналитика</span>
          <div style={{ width: '32px' }} />
        </div>

        <div style={{ padding: '20px 16px', display: 'flex', flexDirection: 'column', gap: '20px' }}>

        {/* Subtitle */}
        <p style={{ fontSize: '14px', color: '#A8B2C8', lineHeight: 1.5, textAlign: 'center', margin: '0 8px' }}>
          Смотрите, как менялось ваше эмоциональное состояние с течением времени.
        </p>

        {effectiveError && (
          <div
            style={{
              background: 'rgba(255,107,107,0.12)',
              border: '1px solid rgba(255,107,107,0.25)',
              borderRadius: '16px',
              padding: '14px 16px',
              color: '#FFD1D1',
              fontSize: '13px',
              lineHeight: 1.5,
            }}
          >
            Не удалось загрузить аналитику: {effectiveError}
          </div>
        )}

        {/* Period filter - scrollable row */}
        <div style={{ overflowX: 'auto', marginLeft: '-16px', marginRight: '-16px', paddingLeft: '16px', paddingRight: '16px' }}>
          <div style={{ display: 'flex', gap: '8px', width: 'max-content' }}>
            {PERIODS.map(p => (
              <button
                key={p.id}
                onClick={() => handlePeriodSelect(p.id)}
                style={{
                  padding: '8px 16px',
                  borderRadius: '20px',
                  border: period === p.id ? 'none' : '1px solid rgba(255,255,255,0.12)',
                  cursor: 'pointer',
                  fontSize: '13px',
                  fontWeight: period === p.id ? 600 : 400,
                  color: period === p.id ? '#fff' : '#A8B2C8',
                  background: period === p.id
                    ? 'linear-gradient(135deg, #3ECFA0, #2BA87F)'
                    : 'rgba(255,255,255,0.05)',
                  transition: 'all 0.2s',
                  whiteSpace: 'nowrap',
                  boxShadow: period === p.id ? '0 4px 12px rgba(62,207,160,0.3)' : 'none',
                }}
              >
                {p.id === 'custom' && <><i className="ri-calendar-2-line" style={{ marginRight: '5px' }} /></>}
                {p.label}
              </button>
            ))}
          </div>
        </div>

        {/* Date range picker */}
        {showDatePicker && period === 'custom' && (
          <DateRangePicker
            startDate={startDate}
            endDate={endDate}
            onSelect={handleDateSelect}
          />
        )}

        {/* Main chart card */}
        <div
          style={{
            background: 'rgba(255,255,255,0.05)',
            border: '1px solid rgba(255,255,255,0.08)',
            borderRadius: '24px',
            padding: '20px 16px 16px',
          }}
        >
          <div style={{ display: 'flex', gap: '8px', marginBottom: '18px', flexWrap: 'wrap' }}>
            {EMOTIONS.map(e => {
              const active = visibleEmotions.has(e.key);
              return (
                <button
                  key={e.key}
                  onClick={() => toggleEmotion(e.key)}
                  style={{
                    padding: '5px 12px',
                    borderRadius: '20px',
                    border: `1.5px solid ${active ? e.color : 'rgba(255,255,255,0.12)'}`,
                    cursor: 'pointer',
                    fontSize: '12px',
                    fontWeight: active ? 600 : 400,
                    color: active ? '#fff' : '#6B7A94',
                    background: active ? `${e.color}22` : 'transparent',
                    transition: 'all 0.2s',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '5px',
                  }}
                >
                  <span
                    style={{
                      width: '7px', height: '7px', borderRadius: '50%',
                      background: active ? e.color : '#6B7A94',
                      display: 'inline-block',
                      flexShrink: 0,
                    }}
                  />
                  {e.label}
                </button>
              );
            })}
          </div>

          {loading || userLoading ? (
            <div style={{ height: `${H + 22}px`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <span style={{ fontSize: '13px', color: '#6B7A94' }}>Загружаем данные...</span>
            </div>
          ) : !shouldFetch ? (
            <div style={{ height: `${H + 22}px`, display: 'flex', alignItems: 'center', justifyContent: 'center', textAlign: 'center', padding: '0 24px' }}>
              <span style={{ fontSize: '13px', color: '#6B7A94', lineHeight: 1.5 }}>Выберите начальную и конечную дату периода.</span>
            </div>
          ) : !hasChartValues ? (
            <div style={{ height: `${H + 22}px`, display: 'flex', alignItems: 'center', justifyContent: 'center', textAlign: 'center', padding: '0 24px' }}>
              <span style={{ fontSize: '13px', color: '#6B7A94', lineHeight: 1.5 }}>За выбранный период пока нет проанализированных воспоминаний.</span>
            </div>
          ) : (
          <div style={{ width: '100%', position: 'relative' }}>
            {/* Y-axis labels */}
            <div
              style={{
                position: 'absolute', left: 0, top: 0, height: `${H}px`,
                display: 'flex', flexDirection: 'column', justifyContent: 'space-between',
              }}
            >
              {[10, 7, 5, 3, 1].map(v => (
                <span key={v} style={{ fontSize: '9px', color: '#6B7A94', lineHeight: 1 }}>{v}</span>
              ))}
            </div>

            <div style={{ paddingLeft: '18px' }}>
              <svg
                viewBox={`0 0 ${W} ${H + 22}`}
                style={{ width: '100%', height: `${H + 22}px`, overflow: 'visible' }}
                preserveAspectRatio="none"
              >
                {/* Grid lines */}
                {[1, 3, 5, 7, 10].map(v => (
                  <line
                    key={v}
                    x1={0} y1={H - (v / 10) * H}
                    x2={W} y2={H - (v / 10) * H}
                    stroke="rgba(255,255,255,0.05)"
                    strokeWidth="1"
                  />
                ))}

                {/* Area fills */}
                {EMOTIONS.filter(e => visibleEmotions.has(e.key)).map(e => (
                  <path
                    key={`area-${e.key}`}
                    d={buildAreaPath(dataMap[e.key], W, H)}
                    fill={e.color}
                    fillOpacity="0.07"
                  />
                ))}

                {/* Lines */}
                {EMOTIONS.filter(e => visibleEmotions.has(e.key)).map(e => (
                  <path
                    key={`line-${e.key}`}
                    d={buildPath(dataMap[e.key], W, H)}
                    fill="none"
                    stroke={e.color}
                    strokeWidth="2.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                ))}

                {/* Dots at last point */}
                {EMOTIONS.filter(e => visibleEmotions.has(e.key)).map(e => {
                  const arr = dataMap[e.key];
                  const n = arr.length;
                  if (n === 0) return null;
                  const x = n === 1 ? W / 2 : W;
                  const y = H - (arr[n - 1] / 10) * H;
                  return (
                    <circle
                      key={`dot-${e.key}`}
                      cx={x}
                      cy={y}
                      r="4"
                      fill={e.color}
                      stroke="rgba(26,27,46,0.9)"
                      strokeWidth="1.5"
                    />
                  );
                })}

                {/* X labels */}
                {labels.map((label, i) => {
                  if (!label) return null;
                  const x = labels.length === 1 ? W / 2 : (i / (labels.length - 1)) * W;
                  return (
                    <text
                      key={`lbl-${i}`}
                      x={x}
                      y={H + 18}
                      textAnchor="middle"
                      fontSize="9"
                      fill="#6B7A94"
                    >
                      {label}
                    </text>
                  );
                })}
              </svg>
            </div>
          </div>
          )}
        </div>

        {/* Insight card */}
        <div
          style={{
            background: `linear-gradient(135deg, ${dominantEmotion.color}18, ${dominantEmotion.color}08)`,
            border: `1px solid ${dominantEmotion.color}30`,
            borderRadius: '20px',
            padding: '16px 18px',
            display: 'flex',
            gap: '14px',
            alignItems: 'flex-start',
          }}
        >
          <div
            style={{
              width: '36px', height: '36px', borderRadius: '12px',
              background: `${dominantEmotion.color}22`,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              flexShrink: 0,
            }}
          >
            <i className="ri-sparkling-2-line" style={{ fontSize: '18px', color: dominantEmotion.color }} />
          </div>
          <div>
            <p style={{ fontSize: '12px', fontWeight: 600, color: dominantEmotion.color, marginBottom: '5px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
              Наблюдение
            </p>
            <p style={{ fontSize: '13px', color: '#D0D8E8', lineHeight: 1.55 }}>
              {analytics?.insight || 'За выбранный период пока недостаточно данных для наблюдения.'}
            </p>
          </div>
        </div>

        {/* Stat cards row */}
        <div>
          <p style={{ fontSize: '15px', fontWeight: 600, color: '#F5F5F7', marginBottom: '12px' }}>
            Средние значения
          </p>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '10px' }}>
            {statsData.map(e => (
              <div
                key={e.key}
                style={{
                  background: 'rgba(255,255,255,0.05)',
                  border: '1px solid rgba(255,255,255,0.07)',
                  borderRadius: '18px',
                  padding: '14px 14px',
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '10px' }}>
                  <div
                    style={{
                      width: '28px', height: '28px', borderRadius: '9px',
                      background: `${e.color}20`,
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                    }}
                  >
                    <span style={{ width: '8px', height: '8px', borderRadius: '50%', background: e.color, display: 'block' }} />
                  </div>
                  <span style={{ fontSize: '13px', fontWeight: 500, color: '#F5F5F7' }}>{e.label}</span>
                </div>
                {/* Mini bar */}
                <div style={{ height: '6px', borderRadius: '3px', background: 'rgba(255,255,255,0.08)', overflow: 'hidden', marginBottom: '8px' }}>
                  <div
                    style={{
                      height: '100%',
                      width: `${(e.avg / 10) * 100}%`,
                      background: `linear-gradient(90deg, ${e.color}, ${e.color}99)`,
                      borderRadius: '3px',
                    }}
                  />
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span style={{ fontSize: '11px', color: '#6B7A94' }}>Среднее</span>
                  <span style={{ fontSize: '13px', fontWeight: 600, color: e.color }}>{e.avg}</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '4px' }}>
                  <span style={{ fontSize: '11px', color: '#6B7A94' }}>Максимум</span>
                  <span style={{ fontSize: '12px', fontWeight: 600, color: '#A8B2C8' }}>{e.max}</span>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div>
          <p style={{ fontSize: '15px', fontWeight: 600, color: '#F5F5F7', marginBottom: '12px' }}>
            Воспоминания с сильными эмоциями
          </p>
          {(analytics?.top_memories ?? []).length === 0 ? (
            <div
              style={{
                background: 'rgba(255,255,255,0.04)',
                border: '1px solid rgba(255,255,255,0.07)',
                borderRadius: '16px',
                padding: '18px 16px',
                color: '#6B7A94',
                fontSize: '13px',
                textAlign: 'center',
              }}
            >
              Здесь появятся воспоминания с самыми выраженными эмоциями.
            </div>
          ) : analytics?.top_memories.map(m => {
            const emotion = EMOTIONS.find((item) => item.key === m.dominant_emotion) ?? EMOTIONS[0];
            return (
            <div
              key={m.id}
              style={{
                background: 'rgba(255,255,255,0.04)',
                border: '1px solid rgba(255,255,255,0.07)',
                borderRadius: '16px',
                padding: '13px 14px',
                marginBottom: '8px',
                display: 'flex',
                alignItems: 'center',
                gap: '12px',
              }}
            >
              <div
                style={{
                  width: '40px', height: '40px', borderRadius: '12px',
                  background: `${emotion.color}20`,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  flexShrink: 0,
                }}
              >
                <i className="ri-heart-3-line" style={{ fontSize: '18px', color: emotion.color }} />
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <p style={{ fontSize: '14px', fontWeight: 600, color: '#F5F5F7', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{m.title}</p>
                <p style={{ fontSize: '12px', color: '#6B7A94', marginTop: '2px' }}>{formatShortDate(m.event_date)}</p>
              </div>
              <div style={{ textAlign: 'right', flexShrink: 0 }}>
                <span style={{ fontSize: '14px', fontWeight: 700, color: emotion.color }}>{m.score}</span>
                <p style={{ fontSize: '10px', color: '#6B7A94', marginTop: '2px' }}>{emotion.label}</p>
              </div>
            </div>
            );
          })}
        </div>

        </div>
      </div>
    </PullToRefresh>
  );
}
